import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Flex, Button, Paragraph, Input, Spinner } from 'theme-ui';
import {
  useSettings,
  useCart,
  useProductByHandle,
} from '@backpackjs/storefront';

import {
  StatefulButton,
  BackInStockModal,
  GiftWithPurchase,
  RebuyModal,
  Favorite,
  Svg,
} from '@snippets';
import {
  useCartStatus,
  useModal,
  useSidebar,
  useBreakpointValue,
  useGiftWithPurchase,
  useGatedProduct,
} from '@hooks';

import store, { useRecoilValue } from '@store';

import { useAddToCart } from '../AddToCart/useAddToCart';
import { themed } from './StickyMobileAddToCart.theme';

export const StickyMobileAddToCart = themed(
  ({
    theme,
    product,
    selectedVariant,
    rebuyProducts,
    isVisible = false,
    syncQuantity: quantity,
    setSyncQuantity: setQuantity,
  }) => {
    const gatedPage = useRecoilValue(store.gatedPage);

    const { enableFullProductAccess } = useGatedProduct({ product, gatedPage });

    const productByHandle = useProductByHandle({
      handle: selectedVariant?.product?.handle,
    });
    const settings = useSettings();
    const cart = useCart();
    const mysteryItems = (settings?.mystery?.items || '').split(',');

    // logic to only show Rebuy mystery item once
    const hasMysteryItem = useMemo(() => {
      return cart?.lines?.some(
        (line) =>
          line?.variant?.product?.handle &&
          mysteryItems?.includes(line?.variant?.product?.handle)
      );
    }, [cart?.updatedAt, mysteryItems]);
    let crossSells = rebuyProducts?.crossSells?.products;
    let upSells = rebuyProducts?.upSells?.products;
    if (hasMysteryItem) {
      crossSells = crossSells?.filter(
        (_product) =>
          _product?.handle && !mysteryItems?.includes(_product?.handle)
      );
      upSells = upSells?.filter(
        (_product) =>
          _product?.handle && !mysteryItems?.includes(_product?.handle)
      );
    }

    const [{ status, isSoldOut, isPreOrder, selectedPlan }, { addToCart }] =
      useAddToCart({
        product: productByHandle.product,
        selectedVariant,
      });

    const { started, finished, success, errors } = status.addItem;
    const [, { openModal }] = useModal();
    const [, { openSidebar }] = useSidebar();
    const notifyMeText = useBreakpointValue([
      'Notify Me',
      'Notify Me When Available',
    ]);
    const gwp = useGiftWithPurchase();

    const [, { setAddingToCartStatus, setUpdatingCartStatus }] =
      useCartStatus();

    const [isMaxed, setIsMaxed] = useState(false);
    const [isOverMax, setIsOverMax] = useState(false);
    const [qtyInCart, setQtyInCart] = useState(0);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const { maxQty, ignoredProducts } = settings?.cart?.bulkOrdering || {};
    const cartItems = cart?.lines;

    const noBackInStockOption =
      isSoldOut && !isPreOrder && product?.tags?.includes('no-bis');

    let text = 'Loading...';
    if (!status?.inventoryStatus?.started && product) {
      if (noBackInStockOption) {
        text = 'Sold Out';
      } else if (isSoldOut && !isPreOrder) {
        text = notifyMeText;
      } else if (isPreOrder) {
        text = 'Pre-order';
      } else if (selectedPlan) {
        text = 'Subscribe';
      } else {
        text = 'Add to Bag';
      }
    }

    const ignoredFromMaxQty = useMemo(
      () =>
        ignoredProducts?.some(
          ({ product: ignoredProduct }) =>
            ignoredProduct.handle === product?.handle
        ),
      [product?.handle, ignoredProducts]
    );

    const checkIfCartIsMaxedOnQtyChange = useCallback(() => {
      if (!maxQty) return false;
      const totalProductsInCart =
        cartItems?.reduce((total, { variant, quantity: itemQty }) => {
          if (
            ignoredProducts?.some(
              ({ product: ignoredProduct }) =>
                ignoredProduct.handle === variant.product.handle
            )
          ) {
            return total;
          }
          return total + itemQty;
        }, 0) || 0;
      setQtyInCart(totalProductsInCart);
      setIsMaxed(totalProductsInCart + quantity >= maxQty);
      setIsOverMax(totalProductsInCart + quantity > maxQty);
      return true;
    }, [quantity, maxQty, cart?.updatedAt, ignoredProducts]);

    const setGlobalCartStatusOnAdding = useCallback(() => {
      setAddingToCartStatus({ started, finished, success, errors });
      setUpdatingCartStatus({ started, finished, success, errors });
      const defaultStatus = {
        started: false,
        finished: false,
        success: false,
        errors: [],
      };
      return () => {
        setAddingToCartStatus(defaultStatus);
        setUpdatingCartStatus(defaultStatus);
      };
    }, [started, finished, success]);

    const handleOutOfStock = useCallback(() => {
      openModal(
        <BackInStockModal
          legacyResourceId={selectedVariant?.legacyResourceId}
          title={product?.title}
        />
      );
    }, [selectedVariant?.id, product?.title]);

    useEffect(() => {
      setGlobalCartStatusOnAdding();
    }, [started, finished, success]);
    useEffect(() => {
      checkIfCartIsMaxedOnQtyChange();
    }, [quantity, maxQty, cart?.updatedAt, ignoredProducts]);

    const isNotifyMe = isSoldOut && !isPreOrder;
    const overMaxDisabled = isOverMax && !isNotifyMe && !ignoredFromMaxQty;

    // Quantity selector handlers
    const disabled = quantity <= 1;

    const handleDecrement = useCallback(() => {
      if (disabled || isUpdating) return;
      setQuantity(quantity - 1);
      setUpdatedAt(new Date());
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 700);
    }, [disabled, quantity, isUpdating]);

    const handleIncrement = useCallback(() => {
      if (isMaxed || isUpdating) return;
      setQuantity(quantity + 1);
      setUpdatedAt(new Date());
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 700);
    }, [isMaxed, quantity, isUpdating]);

    const handleInputChange = useCallback(
      async (e) => {
        const typedQuantity = parseInt(e.target.value, 10);
        if (!typedQuantity) {
          setQuantity(quantity);
        } else if (typedQuantity < 1) {
          setQuantity(1);
        } else if (typedQuantity + qtyInCart > maxQty) {
          const remainder = maxQty - qtyInCart;
          setQuantity(remainder > 0 ? remainder : 1);
        } else {
          setQuantity(typedQuantity);
        }
        setUpdatedAt(new Date());
        setIsUpdating(true);
        setTimeout(() => setIsUpdating(false), 700);
      },
      [quantity, maxQty, qtyInCart]
    );

    if (!enableFullProductAccess || !selectedVariant || !isVisible) return null;

    return (
      <Box data-comp={StickyMobileAddToCart.displayName} sx={theme.wrapper}>
        <Flex sx={theme.content}>
          {/* Custom Quantity Selector */}
          <Flex sx={theme.quantityWrapper}>
            <Button
              variant="buttons.plain"
              onClick={handleDecrement}
              disabled={disabled || isUpdating}
              sx={{
                ...theme.quantityButton,
                cursor: disabled || isUpdating ? 'default' : 'pointer',
                ':hover': {
                  borderColor: disabled || isUpdating ? 'gray' : 'mediumGray',
                },
              }}
            >
              <Svg
                viewBox="0 0 48 48"
                alt="Decrease quantity"
                src="/svgs/minus.svg#minus"
                loading="lazy"
                sx={{
                  ...theme.buttonIcon,
                  color: disabled || isUpdating ? 'mediumDarkGray' : 'text',
                }}
              />
            </Button>

            <Flex
              sx={{
                variant: 'flex.row.center.center',
                flex: 1,
                mx: 2,
                position: 'relative',
                height: '100%',
                width: '44px',
              }}
            >
              <Paragraph
                as={Input}
                key={updatedAt}
                type="number"
                min="1"
                max={maxQty ? maxQty - qtyInCart : null}
                name="quantity"
                tabIndex="-1"
                aria-hidden="true"
                value={quantity}
                onBlur={handleInputChange}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleInputChange(e);
                  }
                }}
                sx={{
                  visibility: isUpdating ? 'hidden' : 'visible',
                  fontSize: 4, // 16px
                  fontFamily: 'body',
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  p: 0,
                  bg: 'transparent',
                  outline: 'none',
                  borderStyle: 'none',
                  ':focus, :hover': {
                    outline: 'none !important',
                  },
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button':
                    {
                      MozAppearance: 'none',
                      margin: 0,
                    },
                  MozAppearance: 'textfield',
                }}
              />

              {isUpdating && (
                <Spinner
                  sx={{
                    width: '20px',
                    color: 'mediumGray',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            </Flex>

            <Button
              variant="buttons.plain"
              onClick={handleIncrement}
              disabled={isMaxed || isUpdating}
              sx={{
                ...theme.quantityButton,
                cursor: isMaxed || isUpdating ? 'default' : 'pointer',
                ':hover': {
                  borderColor: isMaxed || isUpdating ? 'gray' : 'mediumGray',
                },
              }}
            >
              <Svg
                viewBox="0 0 48 48"
                alt="Increase quantity"
                src="/svgs/plus.svg#plus"
                loading="lazy"
                sx={{
                  ...theme.buttonIcon,
                  color: isMaxed || isUpdating ? 'mediumDarkGray' : 'text',
                }}
              />
            </Button>
          </Flex>

          <StatefulButton
            disabled={!product || overMaxDisabled || noBackInStockOption}
            loading={
              !status?.inventoryStatus?.finished &&
              status?.inventoryStatus?.started
            }
            sx={{
              ...theme.button,
              ...(isNotifyMe || overMaxDisabled || noBackInStockOption
                ? theme.button.unavailable
                : theme.button.available),
              cursor:
                !product || overMaxDisabled || noBackInStockOption
                  ? 'default'
                  : 'pointer',
            }}
            {...status.addItem}
            text={{
              default: text,
              started: 'Adding',
              success: 'Added',
              error: 'Failed To Add',
            }}
            onClick={() => {
              if (isNotifyMe) {
                handleOutOfStock();
              } else {
                addToCart({
                  quantity,
                  callback: (_cart) => {
                    if (gwp.isEligible(_cart)) {
                      openModal(<GiftWithPurchase />);
                    } else if (crossSells?.length || upSells?.length) {
                      openModal(
                        <RebuyModal
                          rebuyProducts={rebuyProducts}
                          productHandle={product.handle}
                          disabledOnClose
                        />
                      );
                    } else {
                      openSidebar('cart');
                    }
                  },
                  openCart: false,
                });
              }
            }}
          />

          <Favorite selectedVariant={selectedVariant} isPdp />
        </Flex>
      </Box>
    );
  }
);

StickyMobileAddToCart.displayName = 'StickyMobileAddToCart';
